package com.castly.admin.ui.labels

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.api.AdminUserInfoDto
import com.castly.shared.dto.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private enum class LabelTab { INFO, USERS }

@Composable
fun LabelDetailView(
    label: LabelDetailDto,
    apiClient: ApiClient,
    onUpdated: () -> Unit
) {
    val colors = AppTheme.colors
    val scope = rememberCoroutineScope()
    var activeTab by remember { mutableStateOf(LabelTab.INFO) }
    var editName by remember(label.id) { mutableStateOf(label.name) }
    var editDescription by remember(label.id) { mutableStateOf(label.description ?: "") }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var showCreateUser by remember { mutableStateOf(false) }
    var showAssignExisting by remember { mutableStateOf(false) }
    var allUsers by remember { mutableStateOf<List<AdminUserInfoDto>>(emptyList()) }
    var assignSearch by remember { mutableStateOf("") }
    var assigning by remember { mutableStateOf(false) }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(Spacing.md)
            height(100.percent)
        }
    }) {
        // Header
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
            }
        }) {
            Div {
                H2({
                    style {
                        fontSize(Typography.xl)
                        color(Color(colors.text))
                        margin(0.px)
                    }
                }) { Text(label.name) }
                Span({
                    style {
                        fontSize(12.px)
                        color(Color(if (label.isActive) colors.success else colors.danger))
                    }
                }) { Text(if (label.isActive) "Active" else "Inactive") }
            }

            Button({
                onClick {
                    scope.launch {
                        try {
                            if (label.isActive) apiClient.deactivateLabel(label.id)
                            else apiClient.activateLabel(label.id)
                            onUpdated()
                        } catch (e: Exception) { error = e.message }
                    }
                }
                style {
                    padding(Spacing.sm, Spacing.md)
                    backgroundColor(Color(if (label.isActive) colors.danger else colors.success))
                    color(Color.white)
                    border(0.px)
                    borderRadius(BorderRadius.md)
                    cursor("pointer")
                    fontSize(13.px)
                }
            }) { Text(if (label.isActive) "Deactivate" else "Activate") }
        }

        // Tabs
        Div({
            style {
                display(DisplayStyle.Flex)
                gap(Spacing.sm)
                property("border-bottom", "1px solid ${colors.border}")
                paddingBottom(Spacing.sm)
            }
        }) {
            LabelTab.values().forEach { tab ->
                val isActive = activeTab == tab
                Div({
                    onClick { activeTab = tab }
                    style {
                        padding(Spacing.sm, Spacing.md)
                        cursor("pointer")
                        fontSize(14.px)
                        fontWeight(if (isActive) "600" else "400")
                        color(Color(if (isActive) colors.primary else colors.textMuted))
                        if (isActive) property("border-bottom", "2px solid ${colors.primary}")
                    }
                }) {
                    Text(when (tab) {
                        LabelTab.INFO -> "Info"
                        LabelTab.USERS -> "Users (${label.users.size})"
                    })
                }
            }
        }

        if (error != null) {
            Div({ style { color(Color(colors.danger)); fontSize(12.px) } }) { Text(error!!) }
        }

        when (activeTab) {
            LabelTab.INFO -> {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(Spacing.md)
                        padding(Spacing.md)
                        backgroundColor(Color(colors.surface))
                        borderRadius(BorderRadius.md)
                    }
                }) {
                    Div {
                        Span({ style { fontSize(12.px); color(Color(colors.textMuted)); display(DisplayStyle.Block); marginBottom(4.px) } }) { Text("Name") }
                        Input(InputType.Text) {
                            value(editName)
                            onInput { editName = it.value }
                            style {
                                padding(Spacing.sm)
                                borderRadius(BorderRadius.sm)
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                backgroundColor(Color(colors.background))
                                color(Color(colors.text))
                                fontSize(14.px)
                                width(100.percent)
                            }
                        }
                    }

                    Div {
                        Span({ style { fontSize(12.px); color(Color(colors.textMuted)); display(DisplayStyle.Block); marginBottom(4.px) } }) { Text("Description") }
                        Input(InputType.Text) {
                            value(editDescription)
                            onInput { editDescription = it.value }
                            style {
                                padding(Spacing.sm)
                                borderRadius(BorderRadius.sm)
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                backgroundColor(Color(colors.background))
                                color(Color(colors.text))
                                fontSize(14.px)
                                width(100.percent)
                            }
                        }
                    }

                    Div({
                        style { fontSize(13.px); color(Color(colors.textMuted)) }
                    }) {
                        Text("${label.audiobookCount} audiobooks · ${label.userCount} users")
                    }

                    Button({
                        onClick {
                            scope.launch {
                                saving = true; error = null
                                try {
                                    apiClient.updateLabel(label.id, UpdateLabelRequest(
                                        name = editName.trim().takeIf { it != label.name },
                                        description = editDescription.trim().takeIf { it != (label.description ?: "") }
                                    ))
                                    onUpdated()
                                } catch (e: Exception) { error = e.message ?: "Failed to update" }
                                saving = false
                            }
                        }
                        style {
                            padding(Spacing.sm, Spacing.md)
                            backgroundColor(Color(colors.primary))
                            color(Color.white)
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                            fontSize(13.px)
                            alignSelf(AlignSelf.FlexStart)
                        }
                    }) { Text(if (saving) "Saving..." else "Save Changes") }
                }
            }

            LabelTab.USERS -> {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.SpaceBetween)
                        alignItems(AlignItems.Center)
                        marginBottom(Spacing.sm)
                    }
                }) {
                    Span({ style { fontSize(14.px); fontWeight("600"); color(Color(colors.text)) } }) { Text("Label Users") }
                    Div({ style { display(DisplayStyle.Flex); gap(Spacing.sm) } }) {
                        Button({
                            onClick {
                                showCreateUser = !showCreateUser
                                if (showCreateUser) showAssignExisting = false
                            }
                            style {
                                padding(Spacing.sm, Spacing.md)
                                backgroundColor(Color(colors.primary))
                                color(Color.white)
                                border(0.px)
                                borderRadius(BorderRadius.md)
                                cursor("pointer")
                                fontSize(13.px)
                            }
                        }) { Text(if (showCreateUser) "Cancel" else "+ New User") }
                        Button({
                            onClick {
                                showAssignExisting = !showAssignExisting
                                if (showAssignExisting) {
                                    showCreateUser = false
                                    scope.launch {
                                        try { allUsers = apiClient.listAdminUsers() } catch (_: Exception) {}
                                    }
                                }
                            }
                            style {
                                padding(Spacing.sm, Spacing.md)
                                backgroundColor(Color("transparent"))
                                color(Color(colors.primary))
                                border(1.px, LineStyle.Solid, Color(colors.primary))
                                borderRadius(BorderRadius.md)
                                cursor("pointer")
                                fontSize(13.px)
                            }
                        }) { Text(if (showAssignExisting) "Cancel" else "Assign Existing") }
                    }
                }

                if (showCreateUser) {
                    CreateLabelUserDialog(
                        labelId = label.id,
                        labelName = label.name,
                        apiClient = apiClient,
                        onCreated = { showCreateUser = false; onUpdated() },
                        onCancel = { showCreateUser = false }
                    )
                }

                // Assign existing user panel
                if (showAssignExisting) {
                    val assignedUserIds = label.users.map { it.userId }.toSet()
                    val assignedEmails = label.users.map { it.email }.toSet()
                    val filteredUsers = allUsers.filter { user ->
                        user.username !in assignedUserIds &&
                        user.email !in assignedEmails &&
                        (assignSearch.isBlank() || user.email.contains(assignSearch, ignoreCase = true))
                    }

                    Div({
                        style {
                            padding(Spacing.md)
                            backgroundColor(Color(colors.surface))
                            borderRadius(BorderRadius.md)
                            marginBottom(Spacing.md)
                            border(1.px, LineStyle.Solid, Color(colors.primary))
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(Spacing.sm)
                        }
                    }) {
                        Div({ style { fontWeight("600"); fontSize(14.px); color(Color(colors.text)) } }) {
                            Text("Assign existing user to ${label.name}")
                        }

                        Input(InputType.Text) {
                            value(assignSearch)
                            placeholder("Search by email...")
                            onInput { assignSearch = it.value }
                            style {
                                padding(Spacing.sm)
                                borderRadius(BorderRadius.sm)
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                backgroundColor(Color(colors.background))
                                color(Color(colors.text))
                                fontSize(14.px)
                                width(100.percent)
                            }
                        }

                        if (allUsers.isEmpty()) {
                            Div({ style { fontSize(13.px); color(Color(colors.textMuted)); padding(Spacing.sm) } }) {
                                Text("Loading users...")
                            }
                        } else if (assignSearch.length < 2) {
                            Div({ style { fontSize(13.px); color(Color(colors.textMuted)); padding(Spacing.sm) } }) {
                                Text("Type at least 2 characters to search (${allUsers.size} users)")
                            }
                        } else if (filteredUsers.isEmpty()) {
                            Div({ style { fontSize(13.px); color(Color(colors.textMuted)); padding(Spacing.sm) } }) {
                                Text("No matching users found")
                            }
                        } else {
                            Div({
                                style {
                                    maxHeight(200.px)
                                    overflowY("auto")
                                    display(DisplayStyle.Flex)
                                    flexDirection(FlexDirection.Column)
                                    gap(4.px)
                                }
                            }) {
                                filteredUsers.take(10).forEach { user ->
                                    Div({
                                        style {
                                            display(DisplayStyle.Flex)
                                            justifyContent(JustifyContent.SpaceBetween)
                                            alignItems(AlignItems.Center)
                                            padding(Spacing.sm, Spacing.md)
                                            backgroundColor(Color(colors.surfaceHover))
                                            borderRadius(BorderRadius.sm)
                                            cursor("pointer")
                                        }
                                    }) {
                                        Div {
                                            Div({ style { fontSize(14.px); color(Color(colors.text)) } }) { Text(user.email) }
                                            Div({ style { fontSize(11.px); color(Color(colors.textMuted)) } }) { Text(user.role) }
                                        }
                                        Button({
                                            onClick {
                                                scope.launch {
                                                    assigning = true
                                                    try {
                                                        apiClient.assignUserToLabel(user.username, label.id)
                                                        showAssignExisting = false
                                                        assignSearch = ""
                                                        onUpdated()
                                                    } catch (e: Exception) {
                                                        error = "Failed to assign: ${e.message}"
                                                    }
                                                    assigning = false
                                                }
                                            }
                                            style {
                                                padding(4.px, Spacing.sm)
                                                backgroundColor(Color(colors.primary))
                                                color(Color.white)
                                                border(0.px)
                                                borderRadius(BorderRadius.sm)
                                                cursor("pointer")
                                                fontSize(12.px)
                                            }
                                        }) { Text(if (assigning) "..." else "Assign") }
                                    }
                                }
                            }
                        }
                    }
                }

                if (label.users.isEmpty()) {
                    Div({ style { padding(Spacing.lg); color(Color(colors.textMuted)); fontSize(14.px) } }) {
                        Text("No users assigned to this label yet.")
                    }
                } else {
                    label.users.forEach { user ->
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                justifyContent(JustifyContent.SpaceBetween)
                                alignItems(AlignItems.Center)
                                padding(Spacing.md)
                                backgroundColor(Color(colors.surface))
                                borderRadius(BorderRadius.md)
                                marginBottom(Spacing.sm)
                            }
                        }) {
                            Div {
                                Div({ style { fontWeight("500"); fontSize(14.px); color(Color(colors.text)) } }) { Text(user.email) }
                                Div({ style { fontSize(12.px); color(Color(colors.textMuted)) } }) {
                                    Text("Role: ${user.role ?: "LABEL_EDITOR"} · Assigned: ${user.assignedAt?.take(10) ?: "N/A"}")
                                }
                            }
                            Button({
                                onClick {
                                    scope.launch {
                                        try { apiClient.unassignUser(user.userId); onUpdated() }
                                        catch (e: Exception) { error = "Failed to remove user: ${e.message}" }
                                    }
                                }
                                style {
                                    padding(4.px, Spacing.sm)
                                    backgroundColor(Color("transparent"))
                                    color(Color(colors.danger))
                                    border(1.px, LineStyle.Solid, Color(colors.danger))
                                    borderRadius(BorderRadius.sm)
                                    cursor("pointer")
                                    fontSize(12.px)
                                }
                            }) { Text("Remove") }
                        }
                    }
                }
            }
        }
    }
}
